import dayjs, { type Dayjs } from 'dayjs';

import type { GrowlerPackageType } from 'utils/types/Growler';
import type { GrowlerCustomer } from 'utils/types/GrowlerCustomer';

export const isGrowlersPath = (pathname: string): boolean =>
  pathname === '/growlers' || pathname.startsWith('/growlers/');

const NAV_ORIGIN_KEY = 'clube42-nav-origin';
export const GROWLERS_CATALOG_HASH = 'growlers-e-garrafas';

type NavOrigin = 'growlers' | 'site';

const readNavOrigin = (): NavOrigin | null => {
  try {
    const value = sessionStorage.getItem(NAV_ORIGIN_KEY);
    if (value === 'growlers' || value === 'site') {
      return value;
    }
  } catch {
    // Ignore private mode / quota failures.
  }
  return null;
};

/** Records the tab entry once: growlers paths vs the rest of the site. */
export const captureNavOrigin = (pathname: string): void => {
  if (readNavOrigin()) {
    return;
  }
  const origin: NavOrigin = isGrowlersPath(pathname) ? 'growlers' : 'site';
  try {
    sessionStorage.setItem(NAV_ORIGIN_KEY, origin);
  } catch {
    // Ignore private mode / quota failures.
  }
};

/** Store nav target based on tab entry (hash scrolls to catalog on growlers). */
export const getStoreNavPath = (): string => {
  if (readNavOrigin() === 'growlers') {
    return `/growlers#${GROWLERS_CATALOG_HASH}`;
  }
  return '/beer-store';
};

export const scrollToGrowlersCatalog = (): void => {
  document.getElementById(GROWLERS_CATALOG_HASH)?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
  });
};

export const GROWLER_PACKAGE_LABELS: Record<GrowlerPackageType, string> = {
  growler: 'Growler',
  bottle: 'Garrafa',
};

export const getGrowlerPackageType = (item?: {
  packageType?: GrowlerPackageType;
}): GrowlerPackageType =>
  item?.packageType === 'bottle' ? 'bottle' : 'growler';

export const GROWLER_DELIVERY_TIME_LINES = [
  'Segunda e Terça: 10h às 18h, Quarta à Sábado: 10h às 22h, Domingo: 10h às 14h.',
] as const;

export const GROWLER_DELIVERY_TIME = GROWLER_DELIVERY_TIME_LINES.join(' ');

export const GROWLER_SERVICE_CITY = 'Araraquara';
export const GROWLER_SERVICE_UF = 'SP';

export const GROWLER_SERVICE_AREA_MESSAGE =
  'Este serviço está disponível apenas para Araraquara-SP.';

export const GROWLER_MIN_AGE = 18;

export const GROWLER_UNDERAGE_MESSAGE =
  'A compra de bebidas alcoólicas é permitida apenas para maiores de 18 anos.';

export const formatGrowlerCep = (digits: string): string => {
  const onlyDigits = digits.replace(/\D/g, '');
  return onlyDigits.length > 5
    ? `${onlyDigits.slice(0, 5)}-${onlyDigits.slice(5, 8)}`
    : onlyDigits;
};

const normalizePlaceName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

export const isGrowlerServiceArea = (city: string, uf: string): boolean =>
  normalizePlaceName(city) === normalizePlaceName(GROWLER_SERVICE_CITY) &&
  uf.trim().toUpperCase() === GROWLER_SERVICE_UF;

export const getAgeInYears = (birthDate: Dayjs): number | null => {
  if (!birthDate.isValid() || birthDate.isAfter(dayjs(), 'day')) {
    return null;
  }
  return dayjs().diff(birthDate, 'year');
};

export const isGrowlerUnderage = (birthDate: Dayjs | null): boolean => {
  if (!birthDate) return false;
  const age = getAgeInYears(birthDate);
  return age !== null && age < GROWLER_MIN_AGE;
};

export const formatGrowlerBirthDate = (birthDate: Dayjs): string =>
  birthDate.format('YYYY-MM-DD');

export const formatGrowlerAddressSummary = (
  customer: GrowlerCustomer,
): string[] => {
  const address = customer.address;
  if (!address) {
    return [
      customer.condominium,
      customer.houseNumber ? `Casa/lote ${customer.houseNumber}` : '',
    ].filter(Boolean);
  }

  const streetLine = [address.street, address.number]
    .filter(Boolean)
    .join(', ');
  const cityLine = [address.city, address.uf].filter(Boolean).join(' - ');

  return [
    streetLine,
    address.complement,
    address.neighborhood,
    cityLine,
    address.cep ? `CEP ${address.cep}` : '',
  ].filter((line): line is string => Boolean(line));
};
